import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Section } from "../components/Bits";
import PageHero from "../components/PageHero";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { useSiteText, lines } from "../lib/siteTexts";

const EMPTY = { name: "", email: "", topic: "", message: "", privacy_accepted: false };

function ContactInfo() {
  const t = useSiteText();
  const email = t("footer_tours_email");
  return (
    <aside className="plant-card p-6 md:p-8 space-y-4 text-sm" data-testid="contact-info">
      <h3 className="font-display text-2xl">Direkter Kontakt</h3>
      <p className="flex items-start gap-2">
        <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--wp-olive)" }} />
        <span>{t("contact_direct_text")} <a href={`mailto:${email}`} className="underline font-medium" data-testid="contact-direct-email">{email}</a></span>
      </p>
      <p>Mehr Infos: <a href={t("footer_more_info_url")} target="_blank" rel="noopener noreferrer" className="underline">{t("footer_more_info_label")}</a></p>
    </aside>
  );
}

function PrivacyText({ text }) {
  const word = "Datenschutzerklärung";
  const i = text.indexOf(word);
  const link = <Link to="/impressum#datenschutz" className="underline" data-testid="contact-privacy-link">{word}</Link>;
  if (i < 0) return <span>{text} ({link})</span>;
  return <span>{text.slice(0, i)}{link}{text.slice(i + word.length)}</span>;
}

export default function Contact() {
  const t = useSiteText();
  const topics = lines(t("contact_topics"));
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const input = "w-full px-4 py-3 rounded-xl border bg-white";
  const border = { borderColor: "var(--wp-border)" };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht senden");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero slug="contact"
        defaultImage="/fotos/roter-weg.webp"
        imageAlt="Waldweg mit roter Erde unter blauem Himmel"
        defaultOverline="Kontakt"
        defaultTitle="Schreib uns"
        defaultSubtitle="Fragen, Anregungen, Tour-Wünsche? Wir freuen uns auf deine Nachricht."
      />
      <Section>
        <div className="grid lg:grid-cols-[1fr_360px] gap-8 max-w-5xl mx-auto items-start">
          {sent ? (
            <div className="plant-card p-8 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--wp-olive)" }} />
              <p className="font-display text-2xl">{t("contact_success_text")}</p>
              <button onClick={() => setSent(false)} className="btn-secondary mt-6" data-testid="contact-new-message">Neue Nachricht</button>
            </div>
          ) : (
            <form onSubmit={submit} className="plant-card p-8 space-y-4" data-testid="contact-form">
              <input required placeholder="Dein Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} style={border} data-testid="contact-name" />
              <input required type="email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={border} data-testid="contact-email" />
              <label className="block">
                <span className="overline block mb-1">Worum geht es? *</span>
                <select required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={input} style={border} data-testid="contact-topic">
                  <option value="">Bitte wählen …</option>
                  {topics.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              <textarea required minLength={5} placeholder="Deine Nachricht…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${input} min-h-[140px]`} style={border} data-testid="contact-message" />
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input required type="checkbox" checked={form.privacy_accepted} onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })} className="mt-1" data-testid="contact-privacy" />
                <PrivacyText text={t("contact_privacy_text")} />
              </label>
              <button disabled={sending} className="btn-primary w-full justify-center" data-testid="contact-submit">
                <Send className="w-4 h-4" /> {sending ? "Sende…" : "Senden"}
              </button>
            </form>
          )}
          <ContactInfo />
        </div>
      </Section>
    </div>
  );
}
